/**
 * Minimal API fetch utility for the bubble chat widget.
 *
 * On Frappe Desk pages `window.frappe.csrf_token` is available. On website
 * pages rendered by apps that don't load the Frappe web bundle (e.g. Frappe
 * Wiki v3 — Alpine.js based, no `window.frappe`), we fall back to a CSRF token
 * captured from `get_bubble_chat_settings` (fetched via GET, which needs no
 * CSRF) and stored here for subsequent POSTs.
 */

let _storedCsrf = "";

export function setCsrfToken(token) {
	if (token) _storedCsrf = token;
}

function getCsrfToken() {
	return (
		(window.frappe && window.frappe.csrf_token) ||
		window.csrf_token ||
		_storedCsrf ||
		""
	);
}

/** GET call — CSRF is not enforced on GET, so this works even before we have
 *  a token (used to bootstrap settings + the token itself). */
export async function apiGet(method, params = {}) {
	const qs = new URLSearchParams(params).toString();
	const url = `/api/method/${method}${qs ? `?${qs}` : ""}`;
	const resp = await fetch(url, { method: "GET", credentials: "include" });
	const data = await resp.json();
	if (!resp.ok || data.exc) {
		const msg = data._server_messages || data.exc || `HTTP ${resp.status}`;
		throw new Error(msg);
	}
	return data.message;
}

async function doFetch(url, params) {
	return fetch(url, {
		method: "POST",
		credentials: "include",
		headers: {
			"Content-Type": "application/json",
			"X-Frappe-CSRF-Token": getCsrfToken(),
		},
		body: JSON.stringify(params),
	});
}

export async function apiFetch(method, params = {}) {
	const url = `/api/method/${method}`;
	let resp = await doFetch(url, params);

	// Retry once on stale CSRF token
	if (resp.status === 400 || resp.status === 403) {
		// Try to refresh the CSRF token. Prefer the guest-safe bubble settings
		// endpoint (works without a prior token); fall back to session info.
		try {
			const settings = await apiGet("frappeai.api.get_bubble_chat_settings");
			if (settings && settings.csrf_token) {
				setCsrfToken(settings.csrf_token);
				window.frappe = window.frappe || {};
				window.frappe.csrf_token = settings.csrf_token;
				window.csrf_token = settings.csrf_token;
			}
		} catch (e) {
			// ignore refresh failure
		}
		resp = await doFetch(url, params);
	}

	const data = await resp.json();
	if (!resp.ok || data.exc) {
		const msg = data._server_messages || data.exc || `HTTP ${resp.status}`;
		throw new Error(msg);
	}
	return data.message;
}
